import React, { useState, forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { createWeightReading } from "../../../../services/patient/details";
import DatePicker from "../../../shared-modules/DatePicker/DatePicker";
import useCurrentTime from "../../../shared-modules/DatePicker/useCurrentTime";
import Input from "../../../ui/input";
import styles from "../../../../styles/patient-page-modules/addReadingDialog.module.css";

const schemaValidation = z.object({
  weight: z.coerce
    .number()
    .positive("weight must be a positive value")
    .min(1, "weight is required.")
    .max(999, "invalid weight value"),
  recorded_at: z.coerce.date(),
});

/**
 * WeightForm - Form for adding weight readings
 * Extends the existing AddWeightReading with new layout and chrome
 *
 * @param {Object} props
 * @param {React.ComponentType} props.icon - The reading type icon component
 * @param {string} props.title - The title for this reading type
 * @param {Function} props.onSuccess - Callback when form is successfully submitted
 * @param {Function} props.onLoadingChange - Callback when loading state changes
 */
const WeightForm = forwardRef(({ icon: Icon, title, onSuccess, onLoadingChange }, ref) => {
  const curr = useCurrentTime();
  const [selectedDate, setSelectedDate] = useState(null);
  const queryClient = useQueryClient();
  const [patientId] = useState(() => sessionStorage.getItem("_pt_id"));

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(schemaValidation),
    defaultValues: {
      weight: null,
      recorded_at: null,
    },
  });

  const mutation = useMutation(
    (newReading) => createWeightReading(newReading),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-readings"]);
        toast.success("Reading added successfully");
        setSelectedDate(null);
        reset();
        if (onSuccess) onSuccess();
      },
      onError: () => {
        toast.error("Could not add reading");
      },
      onSettled: () => {
        if (onLoadingChange) onLoadingChange(false);
      },
    }
  );

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleCancelSelect = () => {
    setSelectedDate(null);
  };

  const submit = (values) => {
    if (onLoadingChange) onLoadingChange(true);
    values.patient = patientId;
    values.recorded_at = selectedDate;
    values.reading_type = "weight";
    mutation.mutate(values);
  };

  // Expose submit method to parent via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(submit)();
    },
  }));

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formContainer}>
      {/* Form Chrome - Icon + Title + Subtitle */}
      <div className={styles.formChrome}>
        <div className={styles.readingIcon}>
          <Icon />
        </div>
        <div className={styles.formTitleSection}>
          <h3 className={styles.formTitle}>{title}</h3>
          <p className={styles.formSubtitle}>Add a reading for {title.toLowerCase()}</p>
        </div>
      </div>

      {/* Form Body - 2-column grid */}
      <div className={styles.formGrid}>
        {/* Row 1: Weight | Date */}
        <Input
          {...register("weight")}
          type="number"
          placeholder="Weight"
          autoFocus
          error={errors?.weight?.message}
        />

        <div className={styles.dateWrapper}>
          <div className={styles.datePickerWrapper}>
            <DatePicker
              selectedDate={selectedDate || curr}
              onDateSelect={handleDateChange}
              showTime={true}
              onCancelSelect={handleCancelSelect}
            />
          </div>
          {errors?.recorded_at && (
            <p className={styles.errorMessage}>{errors.recorded_at.message}</p>
          )}
        </div>
      </div>
    </form>
  );
});

WeightForm.displayName = "WeightForm";

export default WeightForm;
